<script setup lang="ts">
import Customer from "@/customer/interfaces/Customer";
import {computed, ref} from "vue";
import {Bell, Check, File, Heart, Printer, ShoppingCart, Syringe} from "@lucide/vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {DialogFooter} from "@/core/components/ui/dialog";
import useCustomerAppointments from "@/appointment/composables/useCustomerAppointments";
import {appointmentApi} from "@/container";
import formatHelper from "@/core/helpers/formatHelper";

const props = defineProps<{
    customer: Customer;
}>();

const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'open-certification-dialog'): void;
}>();


const {data: appointments, isLoading} = useCustomerAppointments(props.customer.id);
const loadingExport = ref(false);

const isCertified = computed(() => {
    return !!props.customer.certification?.validated_at;
});

/**
 * The mobile the appointment reminder is texted to, whether or not the
 * patient has the application.
 */
const mobile = computed(() => props.customer.information?.phone);

const isReminded = computed(() => isCertified.value || !!mobile.value);

const reminderTitle = computed(() => {
    if (isCertified.value) {
        return 'Votre patient a l\'application Apodis';
    }

    return mobile.value
        ? 'Votre patient recevra un rappel par SMS'
        : 'Votre patient n\'a pas l\'application Apodis';
});

const reminderText = computed(() => {
    if (isCertified.value) {
        return 'Il recevra un rappel automatique avant chaque rendez-vous !';
    }

    return mobile.value
        ? `Un SMS lui rappellera chaque rendez-vous 2 h avant, au ${formatHelper.readablePhone(mobile.value)}.`
        : 'Il ne recevra pas de rappel automatique, ni par SMS : aucun mobile n\'est connu dans son dossier.';
});

const handleOnboarding = () => {
    emits('open-certification-dialog');
};

/**
 * Exports the appointments to pdf format.
 */
const exportAppointments = async () => {
    loadingExport.value = true;
    try {
        const blob = await appointmentApi()
            .exportCustomerAppointments(props.customer.id, {active: true});

        const url = window.URL.createObjectURL(blob);

        window.open(url, '_blank');
        window.URL.revokeObjectURL(url);
    } finally {
        loadingExport.value = false;
    }
};
</script>

<template>
  <div class="flex flex-col bg-gray-50 p-6 gap-4">
    <div
      class="bg-white p-3 rounded-lg" :class="{
        'border-2 border-secondary': isReminded,
        'border-2 border-red-400': !isReminded
      }"
    >
      <div class="flex gap-2 items-center">
        <Check v-if="isReminded" class="size-10 inline-flex stroke-[3px] text-secondary" />
        <div>
          <h2>
            {{ reminderTitle }}
          </h2>
          <p>
            {{ reminderText }}
          </p>
        </div>
      </div>
    </div>
    <div class="flex">
      <div class="space-y-2 flex-1">
        <h3>
          {{ isCertified ? 'Il retrouvera dans l\'app' : 'Connectez votre patient qu\'il retrouve :' }}
        </h3>
        <div class="flex gap-2 items-center">
          <Bell class="text-secondary" />
          <p class="text-primary">
            Notification de rappel avant chaque rendez-vous
          </p>
        </div>
        <div class="flex gap-2 items-center">
          <File class="text-secondary" />
          <p class="text-primary">
            Ordonnance de toute la famille
          </p>
        </div>
        <div class="flex gap-2 items-center">
          <Heart class="text-secondary" />
          <p class="text-primary">
            Programmes santé personnalisés
          </p>
        </div>
        <div class="flex gap-2 items-center">
          <Syringe class="text-secondary" />
          <p class="text-primary">
            Suivi vaccinal
          </p>
        </div>
        <div class="flex gap-2 items-center">
          <ShoppingCart class="text-secondary" />
          <p class="text-primary">
            Click & Collect
          </p>
        </div>
        <div v-if="!isCertified" class="mt-6">
          <BaseButton
            class="bg-secondary text-primary" @click="handleOnboarding"
          >
            Connecter le patient
          </BaseButton>
        </div>
      </div>
      <div class="flex-shrink-0">
        <img
          alt="iphone mockup"
          src="@/assets/images/iphone-mockup.png"
          class="w-44 h-auto transform rotate-6 mr-2"
        >
      </div>
    </div>
  </div>
  <DialogFooter class="py-4 px-6 bg-white">
    <BaseButton
      v-if="appointments?.length"
      class="self-end"
      secondary
      density="comfortable"
      :loading="loadingExport"
      :disabled="isLoading"
      @click="exportAppointments"
    >
      <Printer class="w-4 h-4 inline-block mr-2" />
      Imprimer le récap des RDV
    </BaseButton>
    <BaseButton
      class="self-end"
      primary
      density="comfortable"
      @click="$emit('close')"
    >
      Terminer
    </BaseButton>
  </DialogFooter>
</template>

<style scoped>

</style>
